import { useCallback, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { Url } from '@rossum/api-client';
import { EmailTemplate } from '@rossum/api-client/emailTemplates';
import { QueueRule } from '@rossum/api-client/schemas';
import { BreadcrumbItem } from '@rossum/components/breadcrumbs/Breadcrumbs';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import { DeleteConfirmationDialog } from '@rossum/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { Header } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { SelectionPanelActionDescriptor } from '@rossum/components/selection-panel/createSelectionPanelItem';
import { SelectionPanel } from '@rossum/components/selection-panel/SelectionPanel';
import { IconCircleMinus, IconTrash } from '@rossum/ui/icons/tabler';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import {
  DataGridPro,
  GridColDef,
  GridRowSelectionModel,
} from '@rossum/ui/x-data-grid-pro';
import { boldText } from '../../../lib/formaterValues';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { notify } from '../../toaster';
import { useBulkRemoveQueues } from '../hooks/useBulkRemoveQueues';
import { useDeleteRuleWithUndo } from '../hooks/useDeleteRuleWithUndo';
import { usePatchRule } from '../hooks/usePatchRule';
import {
  getUnavailableEmailTemplates,
  useReferencedEmailTemplates,
} from '../hooks/useReferencedEmailTemplates';
import {
  useValidateRuleAgainstQueues,
  ValidationErrorsPerQueue,
} from '../hooks/useValidateRuleAgainstQueues';
import { AddQueuesDialog } from './AddQueuesDialog';
import { ValidationStatusCell } from './ValidationStatusCell';

type QueueRow = {
  id: number;
  queueId: number;
  queueName: string;
  workspaceName: string;
  validation: ValidationErrorsPerQueue | undefined;
  unavailableEmailTemplates: EmailTemplate[];
};

export type RulePageProps = {
  rule: QueueRule;
  ruleQueues: Url[];
  parentPath: string;
  renderBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => React.ReactNode;
  renderTabs: () => React.ReactNode;
};

export const QueuesPage = ({
  rule,
  ruleQueues,
  parentPath,
  renderBreadcrumbs,
  renderTabs,
}: RulePageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { mutate: patchRule, isLoading: isPatchLoading } = usePatchRule();
  const { mutate: deleteRuleWithUndo, isLoading: isDeleting } =
    useDeleteRuleWithUndo();
  const { mutate: bulkRemoveQueues, isLoading: isRemoving } =
    useBulkRemoveQueues();

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const breadcrumbs = useMemo(() => {
    return [
      {
        label: rule.name,
        path: `${parentPath}/rules/${rule.id}/detail`,
      },
      {
        label: intl.formatMessage({
          id: 'features.ruleManager.queues.title',
        }),
      },
    ];
  }, [intl, parentPath, rule.id, rule.name]);

  const [validationResult, setValidationResult] = useState<
    (ValidationErrorsPerQueue | null)[]
  >([]);

  const { mutate: validateRule, isLoading: isValidating } =
    useValidateRuleAgainstQueues();

  const handleValidate = useCallback(() => {
    if (ruleQueues.length > 0) {
      validateRule(
        { rule, queues: ruleQueues },
        {
          onSuccess: validationResults =>
            setValidationResult(validationResults),
          onError: () =>
            notify.error({
              title: intl.formatMessage({
                id: 'features.ruleManager.queues.validation.failed.title',
              }),
            }),
        }
      );
    }
  }, [intl, rule, ruleQueues, validateRule]);

  const { workspacesWithQueues, isLoading } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const referencedTemplates = useReferencedEmailTemplates(rule);

  const queuesWithRule = useMemo<QueueRow[]>(() => {
    return (
      workspacesWithQueues?.flatMap(workspace =>
        workspace.queues
          .filter(queue => R.isIncludedIn(queue.url, ruleQueues))
          .map(queue => ({
            id: queue.id,
            queueId: queue.id,
            queueName: queue.name,
            workspaceName: workspace.name,
            validation:
              validationResult.find(vr => vr?.queue === queue.url) ?? undefined,
            unavailableEmailTemplates: getUnavailableEmailTemplates(
              referencedTemplates,
              queue.url
            ),
          }))
      ) ?? []
    );
  }, [referencedTemplates, ruleQueues, validationResult, workspacesWithQueues]);

  const columns: GridColDef<QueueRow>[] = useMemo(
    () => [
      {
        field: 'queueName',
        headerName: intl.formatMessage({
          id: 'features.ruleManager.queues.column.queue',
        }),
        flex: 1,
        minWidth: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Link
            color="secondary"
            component={RouterLink}
            to={`/queues/${row.queueId}/settings/rules`}
          >
            {[row.workspaceName, row.queueName].join(' > ')}
          </Link>
        ),
      },
      {
        field: 'validation',
        headerName: intl.formatMessage({
          id: 'features.ruleManager.queues.column.validation',
        }),
        flex: 1,
        minWidth: 200,
        disableColumnMenu: true,
        sortComparator: (
          v1: ValidationErrorsPerQueue | undefined,
          v2: ValidationErrorsPerQueue | undefined
        ) => (v1?.errors.length ?? -1) - (v2?.errors.length ?? -1),
        renderCell: ({ row }) => (
          <ValidationStatusCell
            queueValidation={row.validation}
            unavailableEmailTemplates={row.unavailableEmailTemplates}
          />
        ),
      },
    ],
    [intl]
  );

  const selectedQueueIds = useMemo(
    () => Array.from(rowSelectionModel.ids),
    [rowSelectionModel]
  );

  const handleRemoveQueues = useCallback(() => {
    if (selectedQueueIds.length === queuesWithRule.length) {
      setShowDeleteConfirmation(true);
      return;
    }

    const selectedQueues = queuesWithRule
      .filter(q => selectedQueueIds.includes(q.id))
      .map(q => {
        const workspace = workspacesWithQueues?.find(
          ws => ws.name === q.workspaceName
        );
        return workspace?.queues.find(queue => queue.id === q.queueId);
      })
      .filter(isNotNullOrUndefined);

    bulkRemoveQueues(
      { rule, queues: selectedQueues, ruleQueues },
      {
        onSuccess: () => {
          setRowSelectionModel({ type: 'include', ids: new Set() });
        },
      }
    );
  }, [
    bulkRemoveQueues,
    queuesWithRule,
    rule,
    ruleQueues,
    selectedQueueIds,
    workspacesWithQueues,
  ]);

  const handleDeleteRule = useCallback(() => {
    deleteRuleWithUndo(
      { rule },
      {
        onSuccess: () => {
          setShowDeleteConfirmation(false);
          history.push(`${parentPath}/rules`);
        },
      }
    );
  }, [deleteRuleWithUndo, rule, history, parentPath]);

  const handleAddQueues = (queueUrls: string[]) => {
    patchRule(
      {
        patchedRuleId: rule.id,
        patchedRule: {
          queues: [...ruleQueues, ...queueUrls],
        },
      },
      {
        onSuccess: () => {
          notify.default({
            title: intl.formatMessage(
              {
                id: 'features.ruleManager.queues.add.success',
              },
              { count: queueUrls.length }
            ),
          });
        },
        onError: () => {
          notify.error({
            title: intl.formatMessage({
              id: 'features.ruleManager.queues.add.error',
            }),
          });
        },
      }
    );
  };

  const allSelected = selectedQueueIds.length === ruleQueues.length;

  const selectionPanelItems = useMemo(
    () => [
      {
        id: 'remove',
        label: allSelected
          ? intl.formatMessage({
              id: 'features.ruleManager.queues.remove.all.button',
            })
          : intl.formatMessage({
              id: 'features.ruleManager.queues.remove.button',
            }),
        icon: (
          <SvgIcon>{allSelected ? <IconTrash /> : <IconCircleMinus />}</SvgIcon>
        ),
        disabled: isPatchLoading || isRemoving,
        tooltip: intl.formatMessage({
          id: allSelected
            ? 'features.ruleManager.queues.remove.all.tooltip'
            : 'features.ruleManager.queues.remove.tooltip',
        }),
        onClick: handleRemoveQueues,
        'data-cy': 'queues-bulk-remove',
      } satisfies SelectionPanelActionDescriptor,
    ],
    [allSelected, handleRemoveQueues, intl, isPatchLoading, isRemoving]
  );

  const buttons = useMemo(
    () => [
      <Tooltip
        key="queue-page-validate-rules"
        title={intl.formatMessage({
          id: 'features.ruleManager.queues.validate.tooltip',
        })}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => handleValidate()}
          data-cy="queue-page-validate-rules-button"
          startIcon={isValidating ? <CircularProgress size={16} /> : undefined}
          sx={{
            '&:hover': {
              color: 'inherit',
            },
          }}
        >
          {intl.formatMessage({
            id: 'features.ruleManager.queues.validate',
          })}
        </Button>
      </Tooltip>,
      <Button
        key="add-queues"
        variant="contained"
        onClick={() => setDialogOpen(true)}
      >
        {intl.formatMessage({
          id: 'features.ruleManager.queues.addButton',
        })}
      </Button>,
    ],
    [handleValidate, intl, isValidating]
  );

  return (
    <>
      <PageLayout
        fullWidth
        renderHeader={params => (
          <Header
            scrollableDivRef={params.scrollableDivRef}
            breadcrumbs={renderBreadcrumbs(breadcrumbs)}
            buttons={buttons}
            title={rule.name}
            description={rule.description}
            tabs={renderTabs()}
          />
        )}
      >
        <Stack sx={{ flex: 1, minHeight: 0, px: 4, py: 4 }}>
          <Box>
            <SelectionPanel
              visible={selectedQueueIds.length > 0}
              selectedLabel={
                <Typography variant="body2">
                  {intl.formatMessage(
                    {
                      id: 'features.ruleManager.queues.selection.selected',
                    },
                    { count: selectedQueueIds.length }
                  )}
                </Typography>
              }
              onDeselect={() =>
                setRowSelectionModel({ type: 'include', ids: new Set() })
              }
              actions={selectionPanelItems}
              data-cy="queues-selection-panel"
            />
          </Box>
          <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
            <DataGridPro
              loading={isLoading}
              rows={queuesWithRule}
              columns={columns}
              checkboxSelection
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={setRowSelectionModel}
              disableRowSelectionExcludeModel
              hideFooter
              sx={{
                position: 'absolute',
                inset: 0,
                ...commonDataGridStyles,
              }}
            />
          </Box>
        </Stack>
      </PageLayout>
      <DeleteConfirmationDialog
        open={showDeleteConfirmation}
        title={intl.formatMessage({
          id: 'features.queueSettings.rules.deleteRuleConfirmation.title',
        })}
        description={intl.formatMessage(
          {
            id: 'features.queueSettings.rules.deleteRuleConfirmation.text',
          },
          {
            name: rule.name,
            bold: boldText,
          }
        )}
        onConfirm={handleDeleteRule}
        onCancel={() => setShowDeleteConfirmation(false)}
        isLoading={isDeleting}
      />
      <AddQueuesDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleAddQueues}
        isLoading={isPatchLoading}
        existingQueueUrls={rule.queues}
        emailTemplates={referencedTemplates}
      />
    </>
  );
};
